import React, { useEffect } from 'react';
import {useAppDispatch, useAppSelector} from '../../../../../../hooks/hooks';
import {filterProjectType, updateProjectType} from '../../../../../../features/types';
import {TiminatorType} from '../../../../../../features/types/types';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import {FormInput} from '../../../../../../components/shared/form-controls/form-input';
import {FormCheckbox} from '../../../../../../components/shared/form-controls/form-checkbox';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProjectType } from '../../../../../../features/shifts/types';
import BaseForm from '../../../../../../components/shared/form-controls/base-form';
import { NAVIGATION_ROUTES } from '../../../../../../routes/NavigationRoute';
import { canManageProjectTypes } from '../../../../../../features/profile/entityPermissions';

interface EditForm {
    name: string;
    isDefault: boolean;
    isActive: boolean;
}

const ProjectTypeEditForm = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const profile = useAppSelector(store => store.profile);

    const state = location.state as {projectType: ProjectType} | undefined;
    const canEditProjectTypes = canManageProjectTypes(profile);

    useEffect(() => {
        if (!state?.projectType || !canEditProjectTypes) {
            navigate(NAVIGATION_ROUTES.PROJECT_TYPES);
        }
    }, [canEditProjectTypes, navigate, state]);

    const initialValues: EditForm = {
        name: state?.projectType?.name ?? '',
        isDefault: state?.projectType?.default ?? false,
        isActive: state?.projectType?.active ?? false,
    };

    const initialHeader = state?.projectType?.name ?? '';

    if (!state?.projectType || !canEditProjectTypes) {
        return null;
    }

    const validateName = async (name: string): Promise<boolean> => {
        name = name.toLowerCase();
        if (name === '') {
            return Promise.resolve(false);
        }
        return await dispatch(filterProjectType(name)).unwrap().then((values) => {
            return values.length < 1 || values.some(value => value.name.toLowerCase() !== name) || name === initialValues.name.toLowerCase();
        });
    }

    const save = async (values: EditForm) => {
        const type: TiminatorType = {
            id: state?.projectType?.id ?? 0,
            name: values.name,
            active: values.isActive,
            default: values.isDefault,
        }

        return dispatch(updateProjectType(type))
    }

    const validationScheme: Yup.Schema<EditForm> = Yup.object({
        name: Yup.string()
            .max(45, 'Must be 45 characters or less')
            .required('Required')
            .test(
                'type-name-backend-validation',
                'Project type name is taken',
                async (name) => { return await validateName(name)}),
        isDefault: Yup.boolean().required(),
        isActive: Yup.boolean().required(),
    })

    return <>
        <Formik
            initialValues={initialValues}
            validationSchema={validationScheme}
            validateOnChange={true}
            validateOnBlur={true}
            onSubmit={save}>
            <Form>
                <BaseForm contentGap={24}
                    formType='edit'
                    formHeader='edit_project_type'
                    elementName={initialHeader} 
                    savingTitle='Project type' 
                    onClose={() => navigate(NAVIGATION_ROUTES.PROJECT_TYPES)} 
                    onAfterSave={() => navigate(NAVIGATION_ROUTES.PROJECT_TYPES)}>
                    <FormInput name='name' label='Name' placeholder='Project type' />
                    <fieldset>
                        <FormCheckbox name='isDefault' label='Default' />
                        <FormCheckbox name='isActive' label='Active'/>
                    </fieldset>
                </BaseForm>
            </Form>
        </Formik>
    </>
}

export default ProjectTypeEditForm;
